import { useEffect, useRef } from 'react';
import { downloadPhoto, formatBytes, isSameOrigin } from '../../../utils/download';

const FOCUSABLE_SELECTOR = 'button:not([disabled]), [href], input, select, textarea, [tabindex]:not([tabindex="-1"])';

/**
 * Fullscreen photo viewer. Mounted only while open (see PhotoGallery.js).
 *
 * Keyboard: Esc closes, Tab/Shift+Tab cycle within the dialog, arrow keys
 * navigate. The page is dir="rtl", so on-screen "next"/"previous" controls
 * sit at the physical left/right matching reading direction (next = toward
 * the start, i.e. left in RTL) and the arrow keys mirror those positions —
 * ArrowLeft (physically where "next" sits) advances, ArrowRight goes back.
 *
 * Focus handling deliberately does NOT rely on document.activeElement to
 * find what to restore focus to on close: Safari does not move focus to a
 * <button> on click, so that capture would silently fail there. Instead
 * PhotoGallery passes the actual tile DOM node in as `triggerElement`.
 * Because the user can navigate to a different photo before closing, the
 * *current* triggerElement (read via a live ref, not the value at mount) is
 * what gets focused back — landing on the tile for whichever photo was
 * showing when the lightbox closed, not necessarily the one that opened it.
 *
 * Body scroll lock uses the position:fixed + saved-scrollY technique rather
 * than plain `overflow:hidden` — iOS Safari's background can still scroll
 * via touchmove with just `overflow:hidden` on the body.
 */
export default function PhotoLightbox({ photos, index, onClose, onNavigate, triggerElement }) {
  const dialogRef = useRef(null);
  const liveRef = useRef({ photos, index, onClose, onNavigate, triggerElement });

  useEffect(() => {
    liveRef.current = { photos, index, onClose, onNavigate, triggerElement };
  });

  // Body scroll lock (mount/unmount only).
  useEffect(() => {
    const scrollY = window.scrollY;
    const { style } = document.body;
    const prev = { position: style.position, top: style.top, width: style.width, overflow: style.overflow };

    style.position = 'fixed';
    style.top = `-${scrollY}px`;
    style.width = '100%';
    style.overflow = 'hidden';

    return () => {
      style.position = prev.position;
      style.top = prev.top;
      style.width = prev.width;
      style.overflow = prev.overflow;
      window.scrollTo(0, scrollY);
    };
  }, []);

  // Focus trap + keyboard nav (mount/unmount only — reads live state via
  // liveRef so it never needs to resubscribe, which would otherwise steal
  // focus back to the first element every time the photo changes).
  useEffect(() => {
    const dialog = dialogRef.current;
    const focusables = () => Array.from(dialog.querySelectorAll(FOCUSABLE_SELECTOR));
    (focusables()[0] || dialog).focus();

    function handleKeyDown(event) {
      const { photos, index, onClose, onNavigate } = liveRef.current;

      if (event.key === 'Escape') {
        event.preventDefault();
        onClose();
        return;
      }
      if (event.key === 'ArrowLeft') {
        event.preventDefault();
        onNavigate((index + 1) % photos.length);
        return;
      }
      if (event.key === 'ArrowRight') {
        event.preventDefault();
        onNavigate((index - 1 + photos.length) % photos.length);
        return;
      }
      if (event.key === 'Tab') {
        const items = focusables();
        if (!items.length) return;
        const first = items[0];
        const last = items[items.length - 1];
        if (event.shiftKey && document.activeElement === first) {
          event.preventDefault();
          last.focus();
        } else if (!event.shiftKey && document.activeElement === last) {
          event.preventDefault();
          first.focus();
        }
      }
    }

    document.addEventListener('keydown', handleKeyDown);
    return () => {
      document.removeEventListener('keydown', handleKeyDown);
      liveRef.current.triggerElement?.focus?.();
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const photo = photos[index];
  if (!photo) return null;

  const sameOrigin = isSameOrigin(photo.url);
  const size = formatBytes(photo.size);

  return (
    <div
      className="photo-lightbox"
      role="dialog"
      aria-modal="true"
      aria-label="عرض الصورة"
      ref={dialogRef}
      tabIndex={-1}
    >
      <div className="photo-lightbox__backdrop" onClick={onClose} aria-hidden="true" />

      <div className="photo-lightbox__content">
        <div className="photo-lightbox__topbar">
          <button type="button" className="photo-lightbox__close" onClick={onClose} aria-label="إغلاق">
            <svg viewBox="0 0 24 24" width="18" height="18" fill="none" aria-hidden="true" focusable="false">
              <path d="M6 6l12 12M18 6L6 18" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" />
            </svg>
          </button>
        </div>

        <div className="photo-lightbox__stage">
          {photos.length > 1 && (
            <button
              type="button"
              className="photo-lightbox__nav photo-lightbox__nav--next"
              onClick={() => onNavigate((index + 1) % photos.length)}
              aria-label="الصورة التالية"
            >
              <svg viewBox="0 0 24 24" width="20" height="20" fill="none" aria-hidden="true" focusable="false">
                <path d="M15 5l-7 7 7 7" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" />
              </svg>
            </button>
          )}

          <img
            className="photo-lightbox__img"
            src={photo.url}
            alt={`صورة من الحفل رقم ${index + 1}`}
          />

          {photos.length > 1 && (
            <button
              type="button"
              className="photo-lightbox__nav photo-lightbox__nav--prev"
              onClick={() => onNavigate((index - 1 + photos.length) % photos.length)}
              aria-label="الصورة السابقة"
            >
              <svg viewBox="0 0 24 24" width="20" height="20" fill="none" aria-hidden="true" focusable="false">
                <path d="M9 5l7 7-7 7" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" />
              </svg>
            </button>
          )}
        </div>

        <div className="photo-lightbox__footer">
          <span className="photo-lightbox__meta" aria-live="polite">
            <span className="photo-lightbox__counter">
              {index + 1} / {photos.length}
            </span>
            {size && <span className="photo-lightbox__size">{size}</span>}
          </span>

          <button
            type="button"
            className="ceremony-error__retry photo-lightbox__download"
            onClick={() => downloadPhoto(photo)}
          >
            {sameOrigin ? 'تحميل' : 'فتح الصورة'}
          </button>
        </div>
      </div>
    </div>
  );
}
